/**
 * Save data to localStorage with error handling
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 */
export function saveToLocalStorage(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.warn("Failed to save to localStorage:", error);
    }
}

/**
 * Get data from localStorage with error handling
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Retrieved value or default
 */
export function getFromLocalStorage(key, defaultValue = null) {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        console.warn("Failed to get from localStorage:", error);
        return defaultValue;
    }
}

/**
 * Remove data from localStorage with error handling
 * @param {string} key - Storage key
 */
export function removeFromLocalStorage(key) {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.warn("Failed to remove from localStorage:", error);
    }
}

/**
 * Collect user metadata for analytics
 * @returns {Promise<Object>} User metadata object
 */

const getBrowserInfo = () => {
    if (typeof navigator === "undefined") return "Unknown";

    const userAgent = navigator.userAgent;
    let browser = "Unknown";

    if (userAgent.includes("Firefox")) {
        browser = "Firefox";
    } else if (userAgent.includes("Edg/")) {
        browser = "Edge";
    } else if (userAgent.includes("OPR") || userAgent.includes("Opera")) {
        browser = "Opera";
    } else if (userAgent.includes("Chrome")) {
        browser = "Chrome";
    } else if (userAgent.includes("Safari")) {
        browser = "Safari";
    } else if (userAgent.includes("Trident") || userAgent.includes("MSIE")) {
        browser = "Internet Explorer";
    }

    return browser;
};

const getDeviceInfo = () => {
    if (typeof navigator === "undefined") return "Unknown";

    const userAgent = navigator.userAgent;

    if (/Android/i.test(userAgent)) {
        return "Android";
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return /iPad/i.test(userAgent)
            ? "iPad"
            : /iPod/i.test(userAgent)
            ? "iPod"
            : "iPhone";
    } else if (/Windows Phone/i.test(userAgent)) {
        return "Windows Phone";
    }

    if (/Windows/i.test(userAgent)) {
        return "Windows";
    } else if (/Macintosh/i.test(userAgent)) {
        return "Mac";
    } else if (/Linux/i.test(userAgent)) {
        return "Linux";
    }

    return "Unknown";
};

const getOSInfo = () => {
    if (typeof navigator === "undefined") return "Unknown";

    const userAgent = navigator.userAgent;

    if (/Windows NT 10.0/.test(userAgent)) return "Windows 10";
    if (/Windows NT 11/.test(userAgent)) return "Windows 11";
    if (/Windows NT 6.3/.test(userAgent)) return "Windows 8.1";
    if (/Windows NT 6.2/.test(userAgent)) return "Windows 8";
    if (/Mac OS X 10_15/.test(userAgent)) return "macOS Catalina";
    if (/Mac OS X 10_14/.test(userAgent)) return "macOS Mojave";
    if (/Mac OS X 10_13/.test(userAgent)) return "macOS High Sierra";
    if (/Mac OS X/.test(userAgent)) return "macOS";

    return getDeviceInfo();
};

export const collectUserMetadata = async () => {
    try {
        const countryResponse = await fetch("https://ipapi.co/json/");
        const countryData = await countryResponse.json();

        return {
            // Location & IP Info
            country: countryData.country_name || "Unknown",
            countryCode: countryData.country || "Unknown",
            ip: countryData.ip || "Unknown",
            city: countryData.city || "Unknown",
            region: countryData.region || "Unknown",
            timezone: countryData.timezone || "Unknown",
            isp: countryData.org || "Unknown",

            // Browser & System Info
            browser: getBrowserInfo(),
            device: getDeviceInfo(),
            os: getOSInfo(),
            userAgent: navigator.userAgent,

            // Viewport & Screen Info
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
            screen: {
                width: screen.width,
                height: screen.height,
                colorDepth: screen.colorDepth,
                pixelDepth: screen.pixelDepth,
            },

            // Page & Navigation Info
            url: window.location.href,
            hostname: window.location.hostname,
            pathname: window.location.pathname,
            referrer: document.referrer || "Direct",

            // Additional Context
            language: navigator.language,
            languages: navigator.languages
                ? Array.from(navigator.languages)
                : [navigator.language],
            timestamp: new Date().toISOString(),
            cookiesEnabled: navigator.cookieEnabled,
            doNotTrack: navigator.doNotTrack || "Unknown",
            plugins: navigator.plugins ? navigator.plugins.length : 0,
            hardwareConcurrency: navigator.hardwareConcurrency || "Unknown",
            deviceMemory: navigator.deviceMemory || "Unknown",
        };
    } catch (error) {
        console.error("Error fetching metadata:", error);

        return {
            country: "Unknown",
            countryCode: "Unknown",
            ip: "Unknown",
            browser: getBrowserInfo(),
            device: getDeviceInfo(),
            os: getOSInfo(),
            userAgent: navigator.userAgent,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
            screen: {
                width: screen.width,
                height: screen.height,
            },
            url: window.location.href,
            referrer: document.referrer || "Direct",
            language: navigator.language,
            timestamp: new Date().toISOString(),
            error: error.message,
        };
    }
};
